<template>
  <div class="digital-assistant">
    <!-- 模拟话术提示窗口 -->
    <div v-if="showTooltip" class="tooltip-bubble">
      <div class="tooltip-content" :class="{ 'collapsed': isTooltipCollapsed }">
        <div v-if="!isTooltipCollapsed" class="tooltip-message">
          <span class="tooltip-text">您好！我是军校报考助手"砺兵小智"，有问题可以随时咨询我哦！</span>
        </div>
        <div v-else class="tooltip-collapsed">
          <span class="collapsed-text">👋 点击咨询</span>
        </div>
        <button class="tooltip-toggle" @click.stop="toggleTooltipCollapse">
          <span class="toggle-icon">{{ isTooltipCollapsed ? '▶' : '▼' }}</span>
        </button>
        <div class="tooltip-arrow"></div>
      </div>
    </div>

    <!-- 悬浮头像 -->
    <div class="avatar-container" @click="toggleDialog">
      <img src="@/assets/头像.png" alt="数字助手" class="avatar-img">
      <div class="floating-dot"></div>
      <div class="pulse-ring"></div>
    </div>

    <!-- 弹窗 -->
    <div v-if="dialogVisible" class="custom-dialog-overlay" @click.self="toggleDialog">
      <div class="custom-dialog">
        <div class="custom-dialog-header">
          <div class="header-content">
            <img src="@/assets/头像.png" alt="砺兵小智" class="header-avatar">
            <div class="header-info">
              <h3>砺兵小智</h3>
              <span class="status">在线</span>
            </div>
          </div>
          <button class="close-btn" @click="toggleDialog">
            <span class="close-icon">×</span>
          </button>
        </div>

        <div class="custom-dialog-body">
          <div class="chat-section">
            <div class="chat-messages" ref="messagesContainer">
              <!-- 欢迎消息 -->
              <div class="welcome-message">
                <div class="welcome-content">
                  <img src="@/assets/头像.png" alt="砺兵小智" class="welcome-avatar">
                  <p>您好！我是军校报考助手"砺兵小智"，有什么可以帮您？</p>
                </div>
              </div>

              <!-- 消息列表 -->
              <div 
                v-for="(message, index) in messages" 
                :key="index" 
                :class="['message-item', message.type]"
              >
                <div class="message-bubble">
                  <p>{{ formatAnswer(message.content) }}</p>
                  <!-- AI回答仅供参考说明 -->
                  <div v-if="message.type === 'assistant'" class="ai-disclaimer">
                    <span class="disclaimer-icon">⚠️</span>
                    <span class="disclaimer-text">本内容由AI生成，仅供参考，具体请以官方最新政策为准。</span>
                  </div>
                </div>
                <div class="message-footer">
                  <span class="message-time">{{ formatTime(message.timestamp) }}</span>
                  <button 
                    v-if="message.type === 'assistant'"
                    class="speak-btn"
                    :class="{ 'speaking': message.isSpeaking }"
                    @click="toggleSpeaking(message)"
                    :title="message.isSpeaking ? '停止播报' : '播放语音'"
                  >
                    <span>{{ message.isSpeaking ? '⏹️' : '🔊' }}</span>
                  </button>
                </div>
              </div>

              <!-- 加载状态 -->
              <div v-if="isLoading" class="loading-indicator">
                <span class="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
                <span class="loading-text">砺兵小智正在思考...</span>
              </div>
            </div>

            <div class="chat-input">
              <div class="input-wrapper">
                <input 
                  v-model="inputMessage" 
                  placeholder="输入您的问题，如：军校报考条件、体检标准..." 
                  @keyup.enter="sendMessage"
                  class="text-input"
                  :disabled="isLoading"
                >
                <button 
                  @click="sendMessage" 
                  class="send-btn"
                  :disabled="isLoading || !inputMessage.trim()"
                  :class="{ 'loading': isLoading }"
                >
                  <span v-if="!isLoading">发送</span>
                  <span v-else class="send-loading">发送中...</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 底部快捷问题 -->
        <div class="quick-questions">
          <button @click="quickQuestion('军校报考基本条件有哪些？')" class="quick-question-btn" :disabled="isLoading">
            军校报考基本条件有哪些？
          </button>
          <button @click="quickQuestion('哪些军校适合女生报考？')" class="quick-question-btn" :disabled="isLoading">
            哪些军校适合女生报考？
          </button>
          <button @click="quickQuestion('军校体检标准是什么？')" class="quick-question-btn" :disabled="isLoading">
            军校体检标准是什么？
          </button>
          <button @click="quickQuestion('如何填报军校志愿？')" class="quick-question-btn" :disabled="isLoading">
            如何填报军校志愿？
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onUnmounted } from 'vue';
import { baseURL } from "@/api/axiosConfig";

// 状态管理
const dialogVisible = ref(false);
const inputMessage = ref('');
const messagesContainer = ref(null);
const isLoading = ref(false);
const showTooltip = ref(true); // 模拟话术提示窗口显示状态
const isTooltipCollapsed = ref(false); // 提示窗口收起状态

// Mock 用户数据
const userId = ref('user_' + Date.now());

// 消息列表
const messages = ref([]);

// 自动滚动到消息底部
const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

// 格式化时间
const formatTime = (timestamp) => {
  return timestamp.toLocaleTimeString('zh-CN', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

// 格式化AI回答
const formatAnswer = (content) => {
  if (!content) return content;
  
  let result = content;
  
  // 去除开头的问题行和回答标记
  const questionPattern = /^(问题：.*?\n)+/;
  result = result.replace(questionPattern, '');
  result = result.replace(/^回答：/, '');
  result = result.trim();
  
  return result;
};

// 切换对话框
const toggleDialog = () => {
  dialogVisible.value = !dialogVisible.value;
  showTooltip.value = !dialogVisible.value; // 打开对话框时隐藏提示窗口
  
  if (dialogVisible.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
    // 关闭时停止语音播报
    speechSynthesis.cancel();
  }
};

// 切换提示窗口收起/展开
const toggleTooltipCollapse = () => {
  isTooltipCollapsed.value = !isTooltipCollapsed.value;
};

// 语音播报
const speakMessage = (content, msg = null) => {
  if ('speechSynthesis' in window) {
    const text = content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    
    if (speechSynthesis.getVoices().length === 0) {
      speechSynthesis.onvoiceschanged = () => {
        doSpeak(text, msg);
      };
    } else {
      doSpeak(text, msg);
    }
  }
};

// 执行语音播报
const doSpeak = (text, msg) => {
  const voices = speechSynthesis.getVoices();
  let chineseVoice = voices.find(v => v.lang.includes('zh'));
  
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'zh-CN';
  utterance.rate = 0.9;
  
  if (chineseVoice) {
    utterance.voice = chineseVoice;
  }
  
  utterance.onstart = () => {
    if (msg) msg.isSpeaking = true;
  };
  utterance.onend = () => {
    if (msg) msg.isSpeaking = false;
  };
  utterance.onerror = () => {
    if (msg) msg.isSpeaking = false;
  };
  
  speechSynthesis.cancel();
  speechSynthesis.speak(utterance);
};

// 切换语音播报状态
const toggleSpeaking = (msg) => {
  if (msg.isSpeaking) {
    speechSynthesis.cancel();
    msg.isSpeaking = false;
  } else {
    speakMessage(msg.content, msg);
  }
};

// 组件卸载时清理
onUnmounted(() => {
  speechSynthesis.cancel();
});

// 构建历史对话文本
const buildHistoryText = () => {
  return messages.value.map(msg => {
    return `${msg.type === 'assistant' ? 'AI' : '用户'}: ${msg.content}`;
  }).join('\n');
};

// 调用AI接口
const callAIAPI = async (question) => {
  const requestData = {
    userId: userId.value,
    historyText: buildHistoryText(),
    question: question
  };

  const response = await fetch(baseURL + '/interface/aixiaozhi/aixiaozhi', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestData)
  });

  if (!response.ok) {
    throw new Error(`API请求失败: ${response.status}`);
  }

  const data = await response.json();
  
  if (data.code === 200 && data.data && data.data.answer) {
    return data.data.answer;
  } else {
    throw new Error(data.msg || '获取回答失败');
  }
};

// 发送消息
const sendMessage = async () => {
  if (!inputMessage.value.trim() || isLoading.value) {
    return;
  }
  
  // 添加用户消息
  const userMessage = {
    type: 'user',
    content: inputMessage.value.trim(),
    timestamp: new Date()
  };
  messages.value.push(userMessage);
  
  // 清空输入框
  const question = inputMessage.value.trim();
  inputMessage.value = '';
  
  // 滚动到底部
  scrollToBottom();
  
  // 设置加载状态
  isLoading.value = true;
  
  try {
    // 调用AI接口
    const answer = await callAIAPI(question);
    
    // 添加助手回复
    const assistantMessage = {
      type: 'assistant',
      content: answer,
      timestamp: new Date(),
      isSpeaking: false
    };
    messages.value.push(assistantMessage);
    
    scrollToBottom();
  } catch (error) {
    console.error('获取AI回复失败:', error);
    
    // 添加错误提示
    const errorMessage = {
      type: 'assistant',
      content: '抱歉，当前服务暂不可用，请稍后重试。',
      timestamp: new Date(),
      isSpeaking: false
    };
    messages.value.push(errorMessage);
  } finally {
    isLoading.value = false;
    scrollToBottom();
  }
};

// 快速提问
const quickQuestion = (question) => {
  if (!isLoading.value) {
    inputMessage.value = question;
    sendMessage();
  }
};

// 清空对话
const clearChat = () => {
  speechSynthesis.cancel();
  messages.value = [];
};
</script>

<style scoped>
.digital-assistant {
  position: fixed;
  top: 160px;
  right: 20px;
  z-index: 9999;
}

.avatar-container {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4c8AFF 0%, #6b7fff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(76, 138, 255, 0.4);
  transition: all 0.3s ease;
  animation: float 3s ease-in-out infinite;
}

.avatar-container:hover {
  transform: scale(1.15);
  box-shadow: 0 8px 24px rgba(76, 138, 255, 0.6);
}

.floating-dot {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  background-color: #52c41a;
  border-radius: 50%;
  border: 3px solid white;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
}

.avatar-img {
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

/* 脉冲环动画 */
.pulse-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid rgba(76, 138, 255, 0.3);
  animation: pulse 2s ease-out infinite;
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

/* 模拟话术提示窗口 */
.tooltip-bubble {
  position: absolute;
  right: 75px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10000;
}

.tooltip-content {
  position: relative;
}

.tooltip-message {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 2px solid #4c8AFF;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 20px rgba(76, 138, 255, 0.25);
  animation: tooltipFadeIn 0.4s ease, tooltipBounce 2s ease-in-out infinite;
  min-width: 180px;
  max-width: 220px;
}

.tooltip-text {
  font-size: 14px;
  color: #333;
  line-height: 1.4;
  display: block;
}

/* 收起状态样式 */
.tooltip-content.collapsed .tooltip-message {
  display: none;
}

.tooltip-collapsed {
  background: linear-gradient(135deg, #4c8AFF 0%, #6b7fff 100%);
  border-radius: 12px;
  padding: 8px 12px;
  box-shadow: 0 4px 16px rgba(76, 138, 255, 0.3);
  animation: tooltipFadeIn 0.4s ease;
}

.collapsed-text {
  font-size: 13px;
  color: #fff;
  font-weight: 500;
}

/* 切换按钮 */
.tooltip-toggle {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #4c8AFF;
  border: 2px solid #fff;
  color: #fff;
  font-size: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(76, 138, 255, 0.4);
  transition: all 0.2s ease;
  padding: 0;
}

.tooltip-toggle:hover {
  background-color: #3a7bd5;
  transform: scale(1.1);
}

.toggle-icon {
  line-height: 1;
}

.tooltip-arrow {
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid #4c8AFF;
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes tooltipBounce {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(5px); }
}

/* 弹窗遮罩 */
.custom-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 弹窗内容 */
.custom-dialog {
  width: 700px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  max-height: 85vh;
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
}

/* 弹窗头部 */
.custom-dialog-header {
  background: linear-gradient(135deg, #4c8AFF 0%, #3a7ae0 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
}

.header-info {
  display: flex;
  flex-direction: column;
}

.header-info h3 {
  color: white;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.status {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 4px;
}

.status::before {
  content: '';
  width: 8px;
  height: 8px;
  background-color: #52c41a;
  border-radius: 50%;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-icon {
  color: white;
  font-size: 20px;
  line-height: 1;
}

/* 弹窗主体 */
.custom-dialog-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
}

.chat-section {
  display: flex;
  flex-direction: column;
  height: 350px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 欢迎消息 */
.welcome-message {
  text-align: center;
  padding: 20px 0;
}

.welcome-content {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.welcome-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 3px solid #e8f0fe;
}

.welcome-content p {
  margin: 0;
  color: #666;
  font-size: 14px;
  background-color: #f5f7fa;
  padding: 10px 20px;
  border-radius: 20px;
}

/* 消息项 */
.message-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  animation: messageFadeIn 0.3s ease;
}

@keyframes messageFadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.message-item.user {
  align-items: flex-end;
}

.message-item.assistant {
  align-items: flex-start;
}

.message-bubble {
  max-width: 75%;
}

.message-bubble p {
  margin: 0;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.6;
  word-wrap: break-word;
}

.message-item.user .message-bubble p {
  background: linear-gradient(135deg, #4c8AFF 0%, #3a7ae0 100%);
  color: white;
  border-radius: 18px 18px 4px 18px;
}

.message-item.assistant .message-bubble p {
  background-color: #f0f7ff;
  color: #333;
  border: 1px solid #d0e5ff;
  border-radius: 18px 18px 18px 4px;
}

/* AI回答免责声明 */
.ai-disclaimer {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 10px;
  background-color: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  font-size: 12px;
  color: #ad6800;
}

.disclaimer-icon {
  font-size: 14px;
  flex-shrink: 0;
}

.disclaimer-text {
  line-height: 1.3;
}

.message-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.message-time {
  font-size: 12px;
  color: #999;
}

.speak-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 14px;
  opacity: 0.5;
  transition: all 0.2s;
}

.speak-btn:hover {
  opacity: 1;
}

.speak-btn.speaking {
  opacity: 1;
  color: #4c8AFF;
}

/* 加载状态 */
.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px;
}

.loading-dots {
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background-color: #4c8AFF;
  border-radius: 50%;
  animation: loadingDot 1.4s ease-in-out infinite both;
}

.loading-dots span:nth-child(1) { animation-delay: -0.32s; }
.loading-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes loadingDot {
  0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.loading-text {
  font-size: 14px;
  color: #666;
}

/* 输入框区域 */
.chat-input {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
}

.input-wrapper {
  display: flex;
  gap: 10px;
}

.text-input {
  flex: 1;
  padding: 12px 20px;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  font-size: 14px;
  transition: all 0.2s ease;
}

.text-input:focus {
  outline: none;
  border-color: #4c8AFF;
  box-shadow: 0 0 0 3px rgba(76, 138, 255, 0.1);
}

.text-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.send-btn {
  padding: 12px 28px;
  background: linear-gradient(135deg, #4c8AFF 0%, #3a7ae0 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 138, 255, 0.4);
}

.send-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.send-btn.loading {
  pointer-events: none;
}

.send-loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.send-loading::after {
  content: '';
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 底部快捷菜单 */
.quick-questions {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
  background-color: #fafbfc;
}

.quick-question-btn {
  padding: 10px 14px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  color: #666;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.quick-question-btn:hover:not(:disabled) {
  border-color: #4c8AFF;
  background-color: #f0f7ff;
  color: #4c8AFF;
}

.quick-question-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
